import { useCallback, useEffect, useState } from "react";
import { Popconfirm, Tooltip } from "antd";
import { BadgeCheck } from "lucide-react";
import dayjs from "dayjs";
import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../common/ToastMsg";
import { CONTACT_VERIFICATION_URL } from "../../../constants/api";

/**
 * Verified email / mobile badges on a lead's contact cards.
 *
 * Verified means one of:
 *  - the lead opened a confirm-details link sent to that exact address (by
 *    email) or number (by WhatsApp / SMS) and submitted it;
 *  - a staff member marked it verified by hand (after a call, say).
 * The backend decides, so "the same number" follows the CRM's one rule for it.
 * Refetched whenever the contact list changes: editing an email drops its badge.
 */
export function useContactVerification(customerId, contacts) {
    const { fetch, create } = useApiMethods();
    const [flags, setFlags] = useState({});

    const apply = (res) => {
        if (res?.status === "success") {
            setFlags(Object.fromEntries((res.data || []).map(r => [r.contact_id, r])));
        }
        return res;
    };

    useEffect(() => {
        let live = true;
        if (!customerId) return undefined;
        fetch(CONTACT_VERIFICATION_URL(customerId)).then(res => { if (live) apply(res); });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, contacts]);

    /** Mark (verify=true) or clear (verify=false) one contact's email / phone. */
    const setVerified = useCallback(async (contactId, kind, verify) => {
        const res = apply(await create(CONTACT_VERIFICATION_URL(customerId),
            { contact_id: contactId, kind, verify }));
        if (res?.status === "success") {
            ToastMsg("success", verify ? "Marked as verified" : "Verification removed");
        } else {
            ToastMsg("error", res?.message || "Could not update verification");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    return { flags, setVerified };
}

const VIA_LABEL = { email: "the email link", whatsapp: "the WhatsApp link", sms: "the SMS link" };

function describe(v, label) {
    if (!v) return `${label} verified`;
    const when = v.at ? ` on ${dayjs(v.at).format("D MMM YYYY")}` : "";
    return v.via === "manual"
        ? `${label} verified by ${v.by || "staff"}${when}`
        : `${label} verified by the lead through ${VIA_LABEL[v.via] || "a link"}${when}`;
}

export function VerifiedBadge({ title }) {
    return (
        <span title={title}
              className="inline-flex items-center gap-0.5 rounded-full bg-green-50 px-1.5 py-px text-[9px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-300">
            <BadgeCheck className="h-2.5 w-2.5" aria-hidden="true" />
            Verified
        </span>
    );
}

/**
 * The badge, or (for users who can edit the lead) a small Verify button.
 * kind: 'email' | 'phone'.
 */
export function ContactVerify({ verification, contact, kind, canEdit }) {
    const row = verification.flags[contact.id];
    const verified = row?.[`${kind}_verified`];
    const label = kind === "email" ? "Email" : "Mobile";
    const title = describe(row?.[`${kind}_verification`], label);

    if (verified) {
        const badge = <VerifiedBadge title={title} />;
        if (!canEdit) return badge;
        return (
            <Popconfirm title="Remove verification?" description={title} okText="Remove" cancelText="Keep"
                        onConfirm={() => verification.setVerified(contact.id, kind, false)}>
                <button type="button" aria-label={`${title}. Remove verification`} className="leading-none">{badge}</button>
            </Popconfirm>
        );
    }
    if (!canEdit) return null;
    return (
        <Popconfirm title={`Mark this ${label.toLowerCase()} as verified?`}
                    description="Only after you have confirmed it with the lead."
                    okText="Mark verified" cancelText="Cancel"
                    onConfirm={() => verification.setVerified(contact.id, kind, true)}>
            <Tooltip title={`Mark ${label.toLowerCase()} verified`}>
                <button type="button" aria-label={`Mark ${label.toLowerCase()} verified`}
                        className="rounded-full border border-gray-200 px-1.5 py-px text-[9px] font-semibold text-gray-500 hover:border-green-300 hover:text-green-700 dark:border-gray-600 dark:text-gray-400">
                    Verify
                </button>
            </Tooltip>
        </Popconfirm>
    );
}
